import React, { useState } from 'react';
import { Button, Form, InputNumber, Space, Table, Tag, Typography } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ProductVariant } from '../../../types/pz';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/transportCostAllocation';
import { formatUnit } from '../../../utils/units';
import { getProductVariantAttributeEntries, getProductVariantName } from '../../../utils/productSelectorLabel';
import { MAX_AVERAGE_COST_PLN, nonNegativeNumberRule } from './cardFields';
import { formatUnitZloty } from './cardFormat';
import CardSectionHeader from './CardSectionHeader';
import type { AverageCostUnit } from '../productLogistics';

const { Text } = Typography;

type Row = { key: string; base: true } | { key: string; base: false; variant: ProductVariant };

interface VariantsSectionProps {
  productEan?: string | null;
  variants: ProductVariant[];
  showCost: boolean;
  canEdit: boolean;
  canDeactivate: boolean;
  productPrice: number | null | undefined;
  productUnit: AverageCostUnit | null | undefined;
  /** setFieldValue does not fire onValuesChange: tell the page the card is dirty. */
  onCostChanged: () => void;
  onAddVariant: () => void;
  onEditVariant: (variant: ProductVariant) => void;
  onDeactivateVariant: (variant: ProductVariant) => void;
}

/**
 * "Warianty": base product first, then real variants. A variant shows the
 * product cost ("jak produkt podstawowy") until a different cost is set;
 * "Przywróć z produktu" clears the override (null inherits).
 */
const VariantsSection: React.FC<VariantsSectionProps> = ({
  productEan, variants, showCost, canEdit, canDeactivate, productPrice, productUnit,
  onCostChanged, onAddVariant, onEditVariant, onDeactivateVariant,
}) => {
  const { t } = useTranslation(['products', 'common']);
  const form = Form.useFormInstance();
  const variantCosts = Form.useWatch('variantCosts', { form, preserve: true }) as Record<string, number | null | undefined> | undefined;
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const hasProductPrice = productPrice !== null && productPrice !== undefined;
  const unitLabel = productUnit ? formatUnit(productUnit) : null;
  const productCost = hasProductPrice && unitLabel
    ? t('products:card.cost.perUnit', { value: formatUnitZloty(Number(productPrice)), unit: unitLabel })
    : t('products:card.variants.noCost');

  const rows: Row[] = [{ key: 'base', base: true }, ...variants.map((variant) => ({ key: variant.id, base: false as const, variant }))];

  const costCell = (variant: ProductVariant) => {
    const value = variantCosts?.[variant.id];
    const hasOverride = value !== null && value !== undefined;
    const name = getProductVariantName(variant);
    if (hasOverride || editing[variant.id]) {
      return (
        <Space size={8} wrap={false} className="product-card-variant-cost">
          <Form.Item name={['variantCosts', variant.id]} noStyle rules={[nonNegativeNumberRule(t, MAX_AVERAGE_COST_PLN)]}>
            <InputNumber
              className="product-card-number product-card-number--compact"
              aria-label={t('products:card.variants.costAria', { name })}
              placeholder={hasProductPrice ? String(productPrice).replace('.', ',') : undefined}
              suffix={unitLabel ? `zł / ${unitLabel}` : 'zł'}
              disabled={!canEdit}
              controls={false}
              {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
            />
          </Form.Item>
          {canEdit && (
            <Button type="link" size="small" onClick={() => {
              form.setFieldValue(['variantCosts', variant.id], null);
              setEditing((current) => ({ ...current, [variant.id]: false }));
              onCostChanged();
            }}>
              {t('products:card.variants.restore')}
            </Button>
          )}
        </Space>
      );
    }
    return (
      <Space size={8} className="product-card-variant-cost">
        <span className="product-card-inherit">
          {productCost}
          <small>{t('products:card.variants.inherit')}</small>
        </span>
        {canEdit && (
          <Button type="link" size="small" onClick={() => setEditing((current) => ({ ...current, [variant.id]: true }))}>
            {t('products:card.variants.setOther')}
          </Button>
        )}
      </Space>
    );
  };

  return (
    <section className="product-card-section">
      <CardSectionHeader
        title={t('products:card.variants.title')}
        subtitle={t('products:card.variants.subtitle')}
        hint={t('products:card.variants.hint')}
      />
      <Table<Row>
        className="product-card-variants"
        rowKey="key"
        size="small"
        pagination={false}
        dataSource={rows}
        columns={[
          {
            title: t('products:card.variants.version'),
            key: 'version',
            render: (_, row) => (row.base
              ? <Text strong>{t('products:card.variants.base')}</Text>
              : (
                <Space wrap size={[4, 4]}>
                  {getProductVariantAttributeEntries(row.variant).map(([key, value]) => (
                    <Tag key={key}>{key}: {value}</Tag>
                  ))}
                  {row.variant.isActive === false && <Tag>{t('products:status.inactive')}</Tag>}
                </Space>
              )),
          },
          {
            title: t('products:card.variants.ean'),
            key: 'ean',
            className: 'product-card-nowrap',
            render: (_, row) => {
              if (row.base) return productEan || <Text type="secondary">—</Text>;
              return row.variant.eanOverride || <Text type="secondary">{t('products:card.variants.sameEan')}</Text>;
            },
          },
          ...(showCost ? [{
            title: t('products:card.variants.cost'),
            key: 'cost',
            render: (_: unknown, row: Row) => (row.base ? <span>{productCost}</span> : costCell(row.variant)),
          }] : []),
          {
            title: '',
            key: 'actions',
            align: 'right' as const,
            render: (_, row) => (row.base ? null : (
              <Space size={4}>
                {canEdit && (
                  <Button size="small" type="link" icon={<EditOutlined />} onClick={() => onEditVariant(row.variant)}>
                    {t('common:edit')}
                  </Button>
                )}
                {canDeactivate && row.variant.isActive !== false && (
                  <Button size="small" type="link" danger onClick={() => onDeactivateVariant(row.variant)}>
                    {t('products:variantEditor.deactivate')}
                  </Button>
                )}
              </Space>
            )),
          },
        ]}
      />
      {canEdit && (
        <Button className="product-card-add-variant" icon={<PlusOutlined />} onClick={onAddVariant}>
          {t('products:actions.addVariant')}
        </Button>
      )}
    </section>
  );
};

export default VariantsSection;
